"use client";

import { useCallback, useState } from "react";
import SizePickerModal from "@/components/comix/bag/SizePickerModal";
import { EXTENDED_BOOK_ENABLED } from "@/lib/comix/bagConfig";
import {
  EXTENDED_INNER_PAGES,
  extendedUpcharge,
  type BookFormat,
  STANDARD_INNER_PAGES,
  type BookPageCount,
} from "@/lib/comix/book-length";
import { trackEvent } from "@/lib/amplitude";

/**
 * A book whose length was already decided by an earlier purchase keeps it — the script is
 * (or is about to be) built for that length, so a second order for the same book (e.g. the
 * "Get it printed too!" upsell) can't change it. Returns null while the length is still open.
 */
export function lockedPageCount(run: { config?: Record<string, any> | null } | null | undefined): BookPageCount | null {
  const c = run?.config;
  if (!c) return null;
  if (Number(c.innerPageCount) === EXTENDED_INNER_PAGES) return EXTENDED_INNER_PAGES;
  if (c.shopifyOrderId || c.approvalRequestedAt || c.userApproved) return STANDARD_INNER_PAGES;
  return null;
}

/**
 * Starts building the extended script in the background the moment the shopper picks it, so
 * it's ready by the time they've paid (see POST /api/comix/extended/prepare). Fire-and-forget:
 * keepalive lets it survive the redirect to Shopify checkout; any failure is harmless because
 * generate-more builds the script after purchase anyway.
 */
export function prepareExtendedScript(runId: string | undefined, sessionId: string | undefined | null): void {
  if (!runId || !sessionId || typeof window === "undefined") return;
  fetch("/api/comix/extended/prepare", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ runId, sessionId }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * The "Extra Pages, Extra Magic" sheet (SizePickerModal) — gated by EXTENDED_BOOK_ENABLED
 * (NEXT_PUBLIC_COMIX_EXTENDED_BOOK) above. While that env flag is off, pickBookLength still
 * resolves straight to the Standard book and never opens it.
 */
const SIZE_PICKER_VISIBLE = true;

type Pending = {
  runId: string;
  sessionId?: string | null;
  title?: string;
  source: string;
  basePrice?: number;
  format?: BookFormat;
  resolve: (pageCount: BookPageCount | null) => void;
};

/**
 * Asks "which book?" (14 or 30 pages) right after the shopper taps Add to Bag / Checkout.
 *
 *   const { pickBookLength, bookLengthModal } = useBookLengthPicker();
 *   const pageCount = await pickBookLength({ runId, run, source: 'checkout' });
 *   if (!pageCount) return; // closed the sheet
 *
 * Resolves immediately (no sheet) when the extended variants aren't configured or the book's
 * length is already locked by an earlier purchase. Render `bookLengthModal` once in the page.
 */
export function useBookLengthPicker() {
  const [pending, setPending] = useState<Pending | null>(null);

  const pickBookLength = useCallback(
    (opts: {
      runId: string;
      run?: { sessionId?: string | null; config?: Record<string, any> | null } | null;
      title?: string;
      source: string;
      /** Price of the Standard book in dollars — shown per book on each length card. */
      basePrice?: number;
      /** Which product is being bought — sets the Extended upcharge shown (default digital). */
      format?: BookFormat;
      /** The caller already knows this book was bought (check-purchase) — the public status
       *  payload strips the order fields lockedPageCount would otherwise look at. */
      alreadyPurchased?: boolean;
    }) => {
      if (!EXTENDED_BOOK_ENABLED || !SIZE_PICKER_VISIBLE) return Promise.resolve<BookPageCount | null>(STANDARD_INNER_PAGES);
      const locked = lockedPageCount(opts.run) ?? (opts.alreadyPurchased ? STANDARD_INNER_PAGES : null);
      if (locked) return Promise.resolve<BookPageCount | null>(locked);
      return new Promise<BookPageCount | null>((resolve) => {
        setPending({
          runId: opts.runId,
          sessionId: opts.run?.sessionId ?? opts.run?.config?.sessionId ?? null,
          title: opts.title,
          source: opts.source,
          basePrice: opts.basePrice,
          format: opts.format,
          resolve,
        });
      });
    },
    [],
  );

  const bookLengthModal = pending ? (
    <SizePickerModal
      items={[{ id: pending.runId, title: pending.title, extraPagesUpcharge: extendedUpcharge(pending.format), basePrice: pending.basePrice }]}
      onCancel={() => {
        pending.resolve(null);
        setPending(null);
      }}
      onConfirm={(selections) => {
        const pageCount = selections[0]?.pageCount ?? STANDARD_INNER_PAGES;
        trackEvent("Book Length - Selected", { runId: pending.runId, source: pending.source, pageCount });
        if (pageCount === EXTENDED_INNER_PAGES) prepareExtendedScript(pending.runId, pending.sessionId);
        pending.resolve(pageCount);
        setPending(null);
      }}
    />
  ) : null;

  return { pickBookLength, bookLengthModal };
}
